/**
 * Mirrors `security/PermissionCodes.java`. The UI hides what the caller cannot
 * do; the backend still enforces it — this is affordance, not security.
 */
export const PERMISSIONS = {
  USER_READ: 'USER_READ',
  USER_WRITE: 'USER_WRITE',
  ROLE_READ: 'ROLE_READ',
  ROLE_WRITE: 'ROLE_WRITE',
  ORGANIZATION_READ: 'ORGANIZATION_READ',
  ORGANIZATION_WRITE: 'ORGANIZATION_WRITE',
  ORGANIZATION_REVIEW: 'ORGANIZATION_REVIEW',
  LICENSE_READ: 'LICENSE_READ',
  LICENSE_WRITE: 'LICENSE_WRITE',
  LICENSE_REVIEW: 'LICENSE_REVIEW',
  REFERENCE_WRITE: 'REFERENCE_WRITE',
  AUDIT_READ: 'AUDIT_READ',
  PATIENT_READ: 'PATIENT_READ',
  PATIENT_WRITE: 'PATIENT_WRITE',
  DOCTOR_READ: 'DOCTOR_READ',
  DOCTOR_WRITE: 'DOCTOR_WRITE',
  VISIT_READ: 'VISIT_READ',
  VISIT_WRITE: 'VISIT_WRITE',
  PRESCRIPTION_READ: 'PRESCRIPTION_READ',
  PRESCRIPTION_WRITE: 'PRESCRIPTION_WRITE',
  PRESCRIPTION_DISPENSE: 'PRESCRIPTION_DISPENSE',
  // Delivery module (2026-09-20). The fleet is the platform's; a tenant only
  // requests and reads. DELIVERY_EXECUTE and TRACKING_PUSH belong to the
  // driver app and gate nothing on the dashboard.
  DELIVERY_REQUEST: 'DELIVERY_REQUEST',
  DELIVERY_READ: 'DELIVERY_READ',
  DELIVERY_MANAGE: 'DELIVERY_MANAGE',
  DELIVERY_EXECUTE: 'DELIVERY_EXECUTE',
  DRIVER_MANAGE: 'DRIVER_MANAGE',
  VEHICLE_MANAGE: 'VEHICLE_MANAGE',
  TRACKING_PUSH: 'TRACKING_PUSH',
  TRACKING_READ: 'TRACKING_READ',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * The platform administrator holds one permission, `*`, and nothing else is
 * ever listed for that role. Every "does the caller hold X" check goes
 * through here, so the wildcard is understood in exactly one place.
 */
export const WILDCARD_PERMISSION = '*';

export function holdsPermission(held: ReadonlySet<string>, code: string): boolean {
  return held.has(WILDCARD_PERMISSION) || held.has(code);
}

/** `["*"]` is rendered as "all permissions", never as a chip literally named `*`. */
export function isWildcardGrant(permissions?: readonly string[] | null): boolean {
  return !!permissions && permissions.includes(WILDCARD_PERMISSION);
}
